import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  GestureResponderEvent,
  Keyboard,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native'
import Animated, { useSharedValue, withTiming } from 'react-native-reanimated'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { appQueryOptions } from '@/src/queries/view-queries'
import { openExternalUrl } from '@/src/utils/external-url'
import { toAbsoluteUrl } from '@/src/utils/url'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import type { ProfileCardMode } from '../logic/useOnboardingProfileCardAnimation'
import { Icon, symbolName } from '../../../components/ui/Icon'
import { Spinner } from '../../../components/ui/Spinner'
import { colors } from '../../../theme'
import {
  PANEL_BODY_SECONDARY_STYLE,
  PANEL_BODY_STYLE,
  PANEL_HEADER_STYLE,
  PANEL_LABEL_CTA_STYLE,
  PANEL_LABEL_STYLE,
} from '../constants/onboardingStyles'
import { useOnboardingStartTrialMutation } from '../hooks/useOnboardingMutations'
import { useOnboardingProfileCardAnimation } from '../logic/useOnboardingProfileCardAnimation'

const profileAdditionalTextSectionsSchema = z.object({
  acceptPrivacyPolicyAnd: z.string(),
  acceptPrivacyPolicyText: z.string(),
  acceptPrivacyPolicyEulaURI: z.string().url(),
  acceptPrivacyPolicyLinkURI: z.string().url(),
  acceptPrivacyPolicyEulaText: z.string(),
  acceptPrivacyPolicyLinkText: z.string(),
})

export interface OnboardingNameInputContent {
  backButtonText?: string
  headerText?: string
  bodyText?: string
  primaryButtonText?: string
  acceptPrivacyText: string
  firstLegalText: string
  firstLegalLink: string
  acceptAndText: string
  secondLegalText: string
  secondLegalLink: string
}

interface OnboardingNameInputStepProps {
  topInset: number
  bottomInset: number
  profileMode: ProfileCardMode
  returnTo?: string | null
}

export function OnboardingNameInputStep({
  topInset: _topInset,
  bottomInset,
  profileMode,
  returnTo,
}: OnboardingNameInputStepProps) {
  const { t } = useTranslation(['common', 'setup'])

  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
  const keyboardOffset = useSharedValue(0)
  const locale = useContentfulLocale()

  const { isProfileInteractive, profileCardAnimatedStyle, handleBackToIntro } =
    useOnboardingProfileCardAnimation({ profileMode, keyboardOffset })
  const startTrialMutation = useOnboardingStartTrialMutation(returnTo)
  const [firstName, setFirstName] = useState('')

  const { data: app } = useSuspenseQuery(appQueryOptions(locale))
  const profileView = app.onboardingCreateProfile

  const isFormValid = firstName.trim().length > 0 && acceptedPrivacy

  const handleContinue = useCallback(() => {
    if (!isFormValid) return
    startTrialMutation.mutate({ firstName: firstName.trim() })
  }, [firstName, isFormValid, startTrialMutation])

  const profileAdditionalTextSections = useMemo(() => {
    return profileAdditionalTextSectionsSchema.parse(
      profileView?.additionalTextSections,
    )
  }, [profileView?.additionalTextSections])

  const profileContent: OnboardingNameInputContent = useMemo(() => {
    const firstLegalLink = toAbsoluteUrl(
      profileAdditionalTextSections.acceptPrivacyPolicyLinkURI,
    )
    const secondLegalLink = toAbsoluteUrl(
      profileAdditionalTextSections.acceptPrivacyPolicyEulaURI,
    )

    return {
      backButtonText: profileView?.secondaryButtonText ?? undefined,
      headerText: profileView?.textHeader ?? undefined,
      bodyText: profileView?.textContent ?? undefined,
      primaryButtonText: profileView?.primaryButtonText ?? undefined,
      acceptPrivacyText: profileAdditionalTextSections.acceptPrivacyPolicyText,
      firstLegalText: profileAdditionalTextSections.acceptPrivacyPolicyLinkText,
      firstLegalLink,
      acceptAndText: profileAdditionalTextSections.acceptPrivacyPolicyAnd,
      secondLegalText:
        profileAdditionalTextSections.acceptPrivacyPolicyEulaText,
      secondLegalLink,
    }
  }, [profileAdditionalTextSections, profileView])

  const handleOpenFirstLegalLink = useCallback(() => {
    void openExternalUrl(profileContent.firstLegalLink)
  }, [profileContent.firstLegalLink])

  const handleOpenSecondLegalLink = useCallback(() => {
    void openExternalUrl(profileContent.secondLegalLink)
  }, [profileContent.secondLegalLink])

  useEffect(() => {
    if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
      return
    }

    const handleKeyboardShow = (event: {
      duration?: number
      endCoordinates: { height: number }
    }) => {
      keyboardOffset.value = withTiming(
        Math.max(event.endCoordinates.height - bottomInset, 0),
        { duration: event.duration ?? 250 },
      )
    }

    const handleKeyboardHide = (event?: { duration?: number }) => {
      keyboardOffset.value = withTiming(0, {
        duration: event?.duration ?? 250,
      })
    }

    const showSubscription = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      handleKeyboardShow,
    )
    const hideSubscription = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      handleKeyboardHide,
    )

    return () => {
      showSubscription.remove()
      hideSubscription.remove()
    }
  }, [bottomInset, keyboardOffset])

  function handleBackPress(event: GestureResponderEvent): void {
    event.preventDefault()
    Keyboard.dismiss()
    setAcceptedPrivacy(false)
    handleBackToIntro()
  }

  const scrollBottomInset =
    Platform.OS === 'web' ? 32 : Math.max(bottomInset + 12, 20)

  return (
    <Animated.View
      className="rounded-t-[20px] overflow-hidden"
      style={[
        {
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          paddingBottom: Platform.OS === 'web' ? bottomInset : 0,
          backgroundColor: colors.backgroundPrimary,
          pointerEvents: isProfileInteractive ? 'auto' : 'none',
        },
        Platform.OS === 'web' ? { maxHeight: '85%' } : undefined,
        profileCardAnimatedStyle,
      ]}
    >
      <Pressable
        onPress={handleBackPress}
        className="flex-row items-center px-6 pt-4 pb-6"
      >
        <Icon
          name={symbolName('arrow.left', 'arrow_back')}
          size={24}
          color={colors.ink}
        />
        <Text
          className={`ml-2 ${Platform.OS === 'ios' ? 'mt-1.5' : ''}`}
          style={PANEL_LABEL_STYLE}
        >
          {profileContent.backButtonText}
        </Text>
      </Pressable>

      <ScrollView
        bounces={false}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 24,
        }}
        contentInset={{ bottom: scrollBottomInset }}
      >
        <View className="pb-2">
          <Text className="mb-2" style={PANEL_HEADER_STYLE}>
            {profileContent.headerText}
          </Text>
          <Text className="mb-6" style={PANEL_BODY_SECONDARY_STYLE}>
            {profileContent.bodyText}
          </Text>

          <Pressable
            className="flex-row items-start mb-6"
            onPress={() => setAcceptedPrivacy((current) => !current)}
            testID="e2e-onboarding-profile-privacy"
          >
            <View
              testID="e2e-onboarding-profile-privacy-checkbox"
              className={`w-5 h-5 rounded border items-center justify-center mr-3 mt-0.5 ${acceptedPrivacy ? 'bg-primary border-primary' : 'bg-white border-border'}`}
            >
              {acceptedPrivacy && (
                <Icon
                  name={symbolName('checkmark', 'check')}
                  size={14}
                  color={colors.primaryForeground}
                />
              )}
            </View>
            <Text className="flex-1" style={PANEL_BODY_STYLE}>
              {profileContent.acceptPrivacyText}{' '}
              <Text
                className="text-primary underline"
                onPress={handleOpenFirstLegalLink}
              >
                {profileContent.firstLegalText}
              </Text>{' '}
              {profileContent.acceptAndText}{' '}
              <Text
                className="text-primary underline"
                onPress={handleOpenSecondLegalLink}
              >
                {profileContent.secondLegalText}
              </Text>
            </Text>
          </Pressable>

          <Text className="mb-2" style={PANEL_LABEL_STYLE}>
            {t('setup:first_name_label')}
          </Text>
          <TextInput
            className="h-12 rounded-lg border border-border bg-background px-4 py-0 text-base text-ink mb-6"
            style={{
              lineHeight: 20,
              includeFontPadding: false,
            }}
            placeholder={t('setup:first_name_placeholder')}
            placeholderTextColor={colors.muted}
            value={firstName}
            onChangeText={setFirstName}
            autoCapitalize="words"
            autoCorrect={false}
            textAlignVertical="center"
            testID="e2e-onboarding-profile-first-name"
          />

          {startTrialMutation.isPending ? (
            <View className="mt-2 h-12 rounded-xl bg-primary items-center justify-center flex-row">
              <Spinner size="small" color={colors.primaryForeground} />
              <Text
                className="ml-3 text-base font-semibold"
                style={{ color: colors.primaryForeground }}
              >
                {t('common:loading')}
              </Text>
            </View>
          ) : (
            <Pressable
              className={`mt-2 h-12 rounded-xl items-center justify-center ${!isFormValid ? 'bg-gray-300' : 'bg-primary'}`}
              onPress={handleContinue}
              disabled={!isFormValid}
              testID="e2e-onboarding-profile-submit"
            >
              <Text style={PANEL_LABEL_CTA_STYLE}>
                {profileContent.primaryButtonText}
              </Text>
            </Pressable>
          )}
          <View style={{ height: scrollBottomInset }} />
        </View>
      </ScrollView>
    </Animated.View>
  )
}
